import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import LinkIcon from "@mui/icons-material/Link";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import {
  deAllocateBatchAllocationApi,
  retrieveBatchAllocationsByShippingInstructionApi,
} from "../../../../components/api/BatchAllocationApiService";
import {
  allocateBatchToShippingInstructionApi,
  createAddBackBatchApi,
  createBatchForBlendVersionApi,
  retrieveBatchesByBlendVersionApi,
} from "../../../../components/api/BatchApiService";
import {
  retrieveAllBlendsApi,
  retrieveBlendVersionsApi,
  retrieveVersionItemsApi,
} from "../../../../components/api/BlendApiService";
import AddEditBatchDialog from "../../BatchView/Dialogs/AddEditBatchDialog";
import XLSX from "sheetjs-style";

function BatchAllocationsDialog({ open, onClose, shippingInstruction }) {
  const [allocations, setAllocations] = useState([]);
  const [blendItemsCache, setBlendItemsCache] = useState({});
  const [loading, setLoading] = useState(false);

  // De-allocate confirmation
  const [deAllocTarget, setDeAllocTarget] = useState(null);
  const [deAllocating, setDeAllocating] = useState(false);

  // Refresh trigger – increment after any create/link to reload the list
  const [refreshKey, setRefreshKey] = useState(0);

  // ── Create-new-batch dialog (uses AddEditBatchDialog) ──────────────────
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Called by AddEditBatchDialog after a batch is successfully saved.
  // Pre-fills the link dialog with the new batch so the user only needs
  // to enter a quantity and confirm.
  const handleBatchCreated = async (savedBatch, blendId, versionId) => {
    const newBatchId = savedBatch?.id ?? savedBatch;
    setLinkBlendId(String(blendId));
    setLinkVersionId(String(versionId));
    setLinkBatchId(newBatchId ? String(newBatchId) : "");
    setLinkQuantity("");
    setLinkSaving(false);
    try {
      const [blendsRes, versionsRes, batchesRes] = await Promise.all([
        retrieveAllBlendsApi(),
        retrieveBlendVersionsApi(blendId),
        retrieveBatchesByBlendVersionApi(blendId, versionId),
      ]);
      setLinkBlends(blendsRes?.data || []);
      setLinkVersions(versionsRes?.data || []);
      setLinkBatches(batchesRes?.data || []);
    } catch {
      // non-fatal; dropdowns may be empty
    }
    setLinkDialogOpen(true);
  };

  // ── Add Back batch dialog ────────────────────────────────────────────────
  const [addBackDialogOpen, setAddBackDialogOpen] = useState(false);
  const [ab, setAb] = useState({});
  const [addBackSaving, setAddBackSaving] = useState(false);

  const abField = (key) => (e) => setAb((p) => ({ ...p, [key]: e.target.value }));

  const handleOpenAddBack = () => {
    setAb({});
    setAddBackSaving(false);
    setAddBackDialogOpen(true);
  };

  const n = (v) => (v !== undefined && v !== "" ? Number(v) : null);

  const handleSaveAddBack = async () => {
    if (!ab.dietOutNet || Number(ab.dietOutNet) <= 0) {
      toast.error("Please enter a valid DIET Out Net quantity.");
      return;
    }
    try {
      setAddBackSaving(true);
      const batch = {
        batchNumber:          ab.batchNumber          || null,
        primaryId:            ab.primaryId            || null,
        primaryCode:          ab.primaryCode          || null,
        primaryGradeName:     ab.primaryGradeName     || null,
        primaryProcessingDate: ab.primaryProcessingDate || null,
        rawIn:                n(ab.rawIn),
        primaryOut:           n(ab.primaryOut),
        dietIn:               n(ab.dietIn),
        dietId:               ab.dietId              || null,
        dietCode:             ab.dietCode            || null,
        dietGradeName:        ab.dietGradeName        || null,
        dietProcessingDate:   ab.dietProcessingDate   || null,
        dietOutNet:           Number(ab.dietOutNet),
        tobaccoPgEur:         n(ab.tobaccoPgEur),
        packagingMaterialEur: n(ab.packagingMaterialEur),
        processingFeeEur:     n(ab.processingFeeEur),
        totalCostEur:         n(ab.totalCostEur),
        unitPriceEur:         n(ab.unitPriceEur),
        invoicedValueEur:     n(ab.invoicedValueEur),
        remark:               ab.remark              || null,
      };
      await createAddBackBatchApi(
        { shippingInstructionId: shippingInstruction.id, quantityToShip: Number(ab.dietOutNet) },
        batch,
      );
      toast.success("Add Back batch created and allocated to this SI.");
      setAddBackDialogOpen(false);
      setRefreshKey((k) => k + 1);
    } catch (error) {
      console.error(error);
      toast.error("Add Back batch could not be created.");
    } finally {
      setAddBackSaving(false);
    }
  };

  // ── Link-existing-batch dialog ───────────────────────────────────────────
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkBlendId, setLinkBlendId] = useState("");
  const [linkVersionId, setLinkVersionId] = useState("");
  const [linkBatchId, setLinkBatchId] = useState("");
  const [linkQuantity, setLinkQuantity] = useState("");
  const [linkBlends, setLinkBlends] = useState([]);
  const [linkVersions, setLinkVersions] = useState([]);
  const [linkBatches, setLinkBatches] = useState([]);
  const [linkSaving, setLinkSaving] = useState(false);

  const handleOpenLinkDialog = async () => {
    setLinkDialogOpen(true);
    setLinkSaving(false);
    setLinkBlendId("");
    setLinkVersionId("");
    setLinkBatchId("");
    setLinkQuantity("");
    setLinkBlends([]);
    setLinkVersions([]);
    setLinkBatches([]);
    try {
      const res = await retrieveAllBlendsApi();
      setLinkBlends(res?.data || []);
    } catch {
      toast.error("Blends could not be loaded.");
    }
  };

  const handleLinkBlendChange = async (e) => {
    const value = e.target.value;
    setLinkBlendId(value);
    setLinkVersionId("");
    setLinkBatchId("");
    setLinkVersions([]);
    setLinkBatches([]);
    if (!value) return;
    try {
      const res = await retrieveBlendVersionsApi(value);
      setLinkVersions(res?.data || []);
    } catch {
      toast.error("Blend versions could not be loaded.");
    }
  };

  const handleLinkVersionChange = async (e) => {
    const value = e.target.value;
    setLinkVersionId(value);
    setLinkBatchId("");
    setLinkBatches([]);
    if (!linkBlendId || !value) return;
    try {
      const res = await retrieveBatchesByBlendVersionApi(linkBlendId, value);
      setLinkBatches(res?.data || []);
    } catch {
      toast.error("Batches could not be loaded.");
    }
  };

  const handleSaveLink = async () => {
    if (!linkBlendId || !linkVersionId || !linkBatchId || !linkQuantity) {
      toast.error("Please select blend, version, batch and enter quantity.");
      return;
    }
    const siQuantityKg = Number(shippingInstruction?.shipmentQuantity) || 0;
    if (siQuantityKg > 0) {
      const alreadyAllocatedKg = allocations.reduce(
        (sum, a) => sum + (Number(a.allocatedQuantityKg) || 0),
        0,
      );
      const remainingKg = siQuantityKg - alreadyAllocatedKg;
      if (Number(linkQuantity) > remainingKg) {
        toast.error(
          `Quantity exceeds SI capacity. Remaining: ${remainingKg.toLocaleString()} kg (SI: ${siQuantityKg.toLocaleString()} kg, already allocated: ${alreadyAllocatedKg.toLocaleString()} kg).`,
        );
        return;
      }
    }
    try {
      setLinkSaving(true);
      await allocateBatchToShippingInstructionApi(
        linkBlendId,
        linkVersionId,
        linkBatchId,
        {
          shippingInstructionId: shippingInstruction.id,
          quantityToShip: Number(linkQuantity),
          unitPriceEur: null,
          allocationCurrency: null,
          allocatedUnitPriceOriginal: null,
          allocatedFxRateToEur: null,
        },
      );
      toast.success("Batch linked to SI.");
      setLinkDialogOpen(false);
      setRefreshKey((k) => k + 1);
    } catch {
      toast.error("Batch could not be linked.");
    } finally {
      setLinkSaving(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadAllocations = async () => {
      if (!open || !shippingInstruction?.id) {
        return;
      }
      setLoading(true);
      try {
        const resp = await retrieveBatchAllocationsByShippingInstructionApi(
          shippingInstruction.id,
        );
        if (cancelled) return;
        const fetchedAllocations = resp?.data || [];
        setAllocations(fetchedAllocations);

        const uniqueKeys = Array.from(
          new Set(
            fetchedAllocations
              .filter((a) => a.blendId && a.blendVersionId)
              .map((a) => `${a.blendId}-${a.blendVersionId}`),
          ),
        );

        const newCache = {};
        await Promise.all(
          uniqueKeys.map(async (key) => {
            const [blendIdStr, versionIdStr] = key.split("-");
            const blendId = Number(blendIdStr);
            const versionId = Number(versionIdStr);
            if (!blendId || !versionId) return;
            try {
              const itemsResp = await retrieveVersionItemsApi(blendId, versionId);
              if (cancelled) return;
              newCache[key] = itemsResp?.data || [];
            } catch (e) {
              console.error("Failed to load blend items for", key, e);
            }
          }),
        );

        if (!cancelled) {
          setBlendItemsCache(newCache);
        }
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          toast.error("Failed to load batch allocations for this SI");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAllocations();

    return () => {
      cancelled = true;
    };
  }, [open, shippingInstruction?.id, refreshKey]);

  const handleDeAllocate = async () => {
    if (!deAllocTarget) return;
    setDeAllocating(true);
    try {
      await deAllocateBatchAllocationApi(shippingInstruction.id, deAllocTarget.id);
      toast.success(`Allocation #${deAllocTarget.id} removed and quantity restored to batch.`);
      setAllocations((prev) => prev.filter((a) => a.id !== deAllocTarget.id));
      setDeAllocTarget(null);
    } catch (err) {
      console.error(err);
      toast.error("Could not de-allocate batch.");
    } finally {
      setDeAllocating(false);
    }
  };

  const titleSuffix =
    shippingInstruction?.finalShippingInstructionNumber || shippingInstruction?.id
      ? ` - SI ${
          shippingInstruction.finalShippingInstructionNumber ||
          shippingInstruction.id
        }`
      : "";

  const handleDownload = async () => {
    if (!allocations || allocations.length === 0) {
      toast.error("No allocations to export");
      return;
    }
    try {
      const sheetData = [];
      const batchTitleRows = [];
      const blendInfoRows = [];
      const summaryLabelRows = [];
      const summaryValueRows = [];
      const itemsTitleRows = [];
      const itemsHeaderRows = [];

      allocations.forEach((alloc, index) => {
        const blendKey =
          alloc.blendId && alloc.blendVersionId
            ? `${alloc.blendId}-${alloc.blendVersionId}`
            : null;
        const blendItems = blendKey ? blendItemsCache[blendKey] || [] : [];

        // Header row similar to the card title
        const batchRowIndex = sheetData.push([
          `Batch ${alloc.batchNumber || alloc.batchId || ""}`,
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          `Allocation #${alloc.id || ""}`,
        ]);
        batchTitleRows.push(batchRowIndex);

        // Blend and version row
        const blendRowIndex = sheetData.push([
          "Blend:",
          alloc.blendName || alloc.blendId || "-",
          "",
          "",
          "Version:",
          alloc.blendVersionName || alloc.blendVersionId || "-",
        ]);
        blendInfoRows.push(blendRowIndex);

        // Summary labels row
        const summaryLabelRowIndex = sheetData.push([
          "Qty (kg)",
          "Value (EUR)",
          "Currency",
          "Unit Price (Orig)",
          "FX → EUR",
          "Unit Price (EUR)",
          "Status",
        ]);
        summaryLabelRows.push(summaryLabelRowIndex);

        // Summary values row
        const summaryValueRowIndex = sheetData.push([
          alloc.allocatedQuantityKg ?? "",
          alloc.allocatedValueEur ?? "",
          alloc.allocationCurrency || "",
          alloc.allocatedUnitPriceOriginal ?? "",
          alloc.allocatedFxRateToEur ?? "",
          alloc.allocatedUnitPriceEur ?? "",
          alloc.recordStatus || "",
        ]);
        summaryValueRows.push(summaryValueRowIndex);

        // Blank spacer row
        sheetData.push([""]);

        // Blend items header and table
        const itemsTitleRowIndex = sheetData.push([
          `Blend Items (${blendItems.length})`,
        ]);
        itemsTitleRows.push(itemsTitleRowIndex);

        const itemsHeaderRowIndex = sheetData.push([
          "Item Code",
          "Item Name",
          "Quantity",
          "Unit",
          "Unit price",
          "Total price",
          "Price $ / HUF → EUR",
          "Customs Duty",
          "Rate",
          "Customs Duty EUR",
          "Total cost",
          "SI",
        ]);
        itemsHeaderRows.push(itemsHeaderRowIndex);

        blendItems.forEach((bi) => {
          const itemCode =
            bi?.shippingInstruction?.shipmentRequest?.customerGradeMark || "";
          const itemName = bi.item?.buyingGrade || "";
          const quantity = bi.quantity ?? "";
          const unit = bi.unit || "";
          const siNumber =
            bi.shippingInstruction?.finalShippingInstructionNumber ||
            bi.shippingInstructionId ||
            "";

          sheetData.push([
            itemCode,
            itemName,
            quantity,
            unit,
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            siNumber,
          ]);
        });

        // Extra blank row between allocations
        if (index < allocations.length - 1) {
          sheetData.push([""]);
        }
      });

      const sheet = XLSX.utils.aoa_to_sheet(sheetData);
      const maxCols = sheetData.reduce(
        (max, row) => Math.max(max, row.length),
        0,
      );

      const applyRowStyle = (rows, style) => {
        rows.forEach((rowIndex) => {
          for (let c = 0; c < maxCols; c += 1) {
            const cellRef = XLSX.utils.encode_cell({ r: rowIndex - 1, c });
            const cell = sheet[cellRef];
            if (cell) {
              cell.s = { ...(cell.s || {}), ...style };
            }
          }
        });
      };

      // Basic styles to get closer to the on-screen card look
      const batchTitleStyle = {
        font: { bold: true },
        fill: { fgColor: { rgb: "A9D08E" } },
      };
      const labelRowStyle = {
        font: { bold: true },
      };
      const itemsHeaderStyle = {
        font: { bold: true },
        fill: { fgColor: { rgb: "D9EAD3" } },
      };

      applyRowStyle(batchTitleRows, batchTitleStyle);
      applyRowStyle(blendInfoRows, labelRowStyle);
      applyRowStyle(summaryLabelRows, labelRowStyle);
      applyRowStyle(itemsTitleRows, labelRowStyle);
      applyRowStyle(itemsHeaderRows, itemsHeaderStyle);

      // Merge cells for nicer layout (Batch title and Blend Items title)
      sheet["!merges"] = [
        // Batch title: merge A..J for each block
        ...batchTitleRows.map((r) => ({
          s: { r: r - 1, c: 0 },
          e: { r: r - 1, c: 9 },
        })),
        // Blend Items title: merge A..L
        ...itemsTitleRows.map((r) => ({
          s: { r: r - 1, c: 0 },
          e: { r: r - 1, c: 11 },
        })),
      ];
      sheet["!cols"] = [
        { wch: 18 },
        { wch: 20 },
        { wch: 10 },
        { wch: 10 },
        { wch: 22 },
        { wch: 18 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "Batch Allocations");

      const siLabel =
        shippingInstruction?.finalShippingInstructionNumber ||
        shippingInstruction?.id ||
        "unknown";
      const fileName = `BatchAllocations_SI_${siLabel}.xlsx`;

      XLSX.writeFile(workbook, fileName);
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate Excel file");
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <span>
            Batch Allocations
            {titleSuffix}
          </span>
          <IconButton
            size="small"
            aria-label="Download batch allocations"
            onClick={handleDownload}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Typography variant="body2" color="textSecondary">
            Loading batch allocations...
          </Typography>
        ) : allocations && allocations.length > 0 ? (
          allocations.map((alloc) => {
            const blendKey =
              alloc.blendId && alloc.blendVersionId
                ? `${alloc.blendId}-${alloc.blendVersionId}`
                : null;
            const blendItems = blendKey ? blendItemsCache[blendKey] || [] : [];

            return (
              <Box
                key={alloc.id}
                sx={{
                  borderRadius: 1,
                  border: "1px solid #e5e7eb",
                  backgroundColor: "#a9d08e",
                  p: 1.5,
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#111827",
                  }}
                >
                  <span>Batch {alloc.batchId + "-" + alloc.primaryId}</span>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <span style={{ color: "#6b7280", fontWeight: 400 }}>
                      Allocation #{alloc.id}
                    </span>
                    <Tooltip title="De-allocate (restore to batch)">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeAllocTarget(alloc)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box
                  sx={{
                    fontSize: 11,
                    color: "#4b5563",
                    mb: 0.75,
                  }}
                >
                  <strong>Blend:</strong> {alloc.blendName || alloc.blendId || "-"}
                  {"  "}
                  <strong>Version:</strong>{" "}
                  {alloc.blendVersionName || alloc.blendVersionId || "-"}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    fontSize: 11,
                    color: "#374151",
                  }}
                >
                  <Box sx={{ mr: 2 }}>
                    <strong>Qty:</strong> {alloc.allocatedQuantityKg} kg
                  </Box>
                  <Box sx={{ mr: 2 }}>
                    <strong>Value:</strong> {alloc.allocatedValueEur} EUR
                  </Box>
                  <Box sx={{ mr: 2 }}>
                    <strong>Currency:</strong> {alloc.allocationCurrency}
                  </Box>
                  <Box sx={{ mr: 2 }}>
                    <strong>Unit Price (Orig):</strong>{" "}
                    {alloc.allocatedUnitPriceOriginal}
                  </Box>
                  <Box sx={{ mr: 2 }}>
                    <strong>FX → EUR:</strong> {alloc.allocatedFxRateToEur}
                  </Box>
                  <Box sx={{ mr: 2 }}>
                    <strong>Unit Price (EUR):</strong> {alloc.allocatedUnitPriceEur}
                  </Box>
                  <Box sx={{ mr: 2 }}>
                    <strong>Status:</strong> {alloc.recordStatus}
                  </Box>
                </Box>

                {blendItems.length > 0 && (
                  <Box
                    sx={{
                      mt: 1,
                      pt: 1,
                      borderTop: "1px dashed #d1d5db",
                      fontSize: 11,
                      color: "#4b5563",
                      overflowX: "auto",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontSize: 11, mb: 0.5 }}
                    >
                      Blend Items ({blendItems.length})
                    </Typography>
                    <Table size="small" sx={{ minWidth: 650 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell align="center" sx={{ fontSize: 11 }}>
                            Item Code
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: 11 }}>
                            Item Name
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: 11 }}>
                            Quantity
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: 11 }}>
                            Unit
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: 11 }}>
                            Unit price
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: 11 }}>
                            Total price
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: 11 }}>
                            Price $ / HUF → EUR
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: 11 }}>
                            Customs Duty
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: 11 }}>
                            Rate
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: 11 }}>
                            Customs Duty EUR
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: 11 }}>
                            Total cost
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: 11 }}>
                            SI
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {blendItems.map((bi, idx) => {
                          const itemCode =
                            bi?.shippingInstruction?.shipmentRequest?.customerGradeMark ||
                            "";
                          const itemName = bi.item?.buyingGrade || "";
                          const quantity = bi.quantity;
                          const unit = bi.unit;
                          const siNumber =
                            bi.shippingInstruction?.finalShippingInstructionNumber ||
                            bi.shippingInstructionId ||
                            "";

                          return (
                            <TableRow key={bi.id || idx}>
                              <TableCell align="center" sx={{ fontSize: 11 }}>
                                {itemCode}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: 11 }}>
                                {itemName}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: 11 }}>
                                {quantity}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: 11 }}>
                                {unit}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: 11 }}>
                                {""}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: 11 }}>
                                {""}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: 11 }}>
                                {""}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: 11 }}>
                                {""}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: 11 }}>
                                {""}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: 11 }}>
                                {""}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: 11 }}>
                                {""}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: 11 }}>
                                {siNumber}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            );
          })
        ) : (
          <Typography variant="body2" color="textSecondary">
            No batch allocations for this Shipping Instruction.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create New Batch
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<LinkIcon />}
            onClick={handleOpenLinkDialog}
          >
            Link Existing Batch
          </Button>
          {/*<Button
            size="small"
            variant="outlined"
            color="warning"
            onClick={handleOpenAddBack}
          >
            Add Back Batch
          </Button>*/}
        </Box>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>

    {/* De-allocate confirmation dialog */}
    <Dialog
      open={!!deAllocTarget}
      onClose={() => setDeAllocTarget(null)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>De-allocate Batch</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Remove allocation{" "}
          <strong>#{deAllocTarget?.id}</strong> (Batch{" "}
          <strong>{deAllocTarget?.batchNumber || deAllocTarget?.batchId}</strong>,{" "}
          {deAllocTarget?.allocatedQuantityKg} kg) from this SI?{" "}
          The quantity will be restored to the batch's stock.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeAllocTarget(null)} disabled={deAllocating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDeAllocate}
          disabled={deAllocating}
        >
          {deAllocating ? <CircularProgress size={18} /> : "De-allocate"}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Create new batch – full AddEditBatchDialog, then auto-opens link dialog */}
    <AddEditBatchDialog
      open={createDialogOpen}
      onClose={() => setCreateDialogOpen(false)}
      onSaved={handleBatchCreated}
      editBatch={null}
    />

    {/* Add Back batch dialog */}
    <Dialog open={addBackDialogOpen} onClose={() => setAddBackDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>Add Back Batch</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Fields marked * are required. Add Back batches do not affect inventory.
        </Typography>

        {/* ── Identification ── */}
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 700 }}>Identification</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <TextField size="small" label="Batch Number" value={ab.batchNumber || ""} onChange={abField("batchNumber")} />
          <TextField size="small" label="Primary ID" value={ab.primaryId || ""} onChange={abField("primaryId")} />
          <TextField size="small" label="Primary Code" value={ab.primaryCode || ""} onChange={abField("primaryCode")} />
          <TextField size="small" label="Primary Grade Name" value={ab.primaryGradeName || ""} onChange={abField("primaryGradeName")} />
          <TextField size="small" label="Primary Processing Date" type="date" InputLabelProps={{ shrink: true }} value={ab.primaryProcessingDate || ""} onChange={abField("primaryProcessingDate")} />
        </Box>

        {/* ── Primary quantities ── */}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 700 }}>Primary Process</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <TextField size="small" label="Raw In (kg)" type="number" value={ab.rawIn || ""} onChange={abField("rawIn")} />
          <TextField size="small" label="Primary Out (kg)" type="number" value={ab.primaryOut || ""} onChange={abField("primaryOut")} />
        </Box>

        {/* ── DIET process ── */}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 700 }}>DIET Process</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <TextField size="small" label="DIET ID" value={ab.dietId || ""} onChange={abField("dietId")} />
          <TextField size="small" label="DIET Code" value={ab.dietCode || ""} onChange={abField("dietCode")} />
          <TextField size="small" label="DIET Grade Name" value={ab.dietGradeName || ""} onChange={abField("dietGradeName")} />
          <TextField size="small" label="DIET Processing Date" type="date" InputLabelProps={{ shrink: true }} value={ab.dietProcessingDate || ""} onChange={abField("dietProcessingDate")} />
          <TextField size="small" label="DIET In (kg)" type="number" value={ab.dietIn || ""} onChange={abField("dietIn")} />
          <TextField size="small" label="DIET Out Net (kg) *" type="number" required value={ab.dietOutNet || ""} onChange={abField("dietOutNet")} />
        </Box>

        {/* ── Costs ── */}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 700 }}>Costs (EUR)</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <TextField size="small" label="Tobacco + PG (EUR)" type="number" value={ab.tobaccoPgEur || ""} onChange={abField("tobaccoPgEur")} />
          <TextField size="small" label="Packaging Material (EUR)" type="number" value={ab.packagingMaterialEur || ""} onChange={abField("packagingMaterialEur")} />
          <TextField size="small" label="Processing Fee (EUR)" type="number" value={ab.processingFeeEur || ""} onChange={abField("processingFeeEur")} />
          <TextField size="small" label="Total Cost (EUR)" type="number" value={ab.totalCostEur || ""} onChange={abField("totalCostEur")} />
          <TextField size="small" label="Unit Price (EUR/kg)" type="number" value={ab.unitPriceEur || ""} onChange={abField("unitPriceEur")} />
          <TextField size="small" label="Invoiced Value (EUR)" type="number" value={ab.invoicedValueEur || ""} onChange={abField("invoicedValueEur")} />
        </Box>

        {/* ── Remark ── */}
        <TextField fullWidth size="small" label="Remark" multiline rows={2} sx={{ mt: 2 }} value={ab.remark || ""} onChange={abField("remark")} />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAddBackDialogOpen(false)} disabled={addBackSaving}>Cancel</Button>
        <Button variant="contained" color="warning" onClick={handleSaveAddBack} disabled={addBackSaving}>
          {addBackSaving ? <CircularProgress size={18} /> : "Create Add Back"}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Link existing batch dialog */}
    <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Link Existing Batch</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal">
          <InputLabel id="link-blend-label">Blend</InputLabel>
          <Select
            labelId="link-blend-label"
            value={linkBlendId}
            label="Blend"
            onChange={handleLinkBlendChange}
          >
            {linkBlends.map((b) => (
              <MenuItem key={b.id} value={b.id}>{b.name || b.code || b.id}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal" disabled={!linkBlendId}>
          <InputLabel id="link-version-label">Blend Version</InputLabel>
          <Select
            labelId="link-version-label"
            value={linkVersionId}
            label="Blend Version"
            onChange={handleLinkVersionChange}
          >
            {linkVersions.map((v) => (
              <MenuItem key={v.id} value={v.id}>{v.versionName || v.id}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal" disabled={!linkVersionId}>
          <InputLabel id="link-batch-label">Batch</InputLabel>
          <Select
            labelId="link-batch-label"
            value={linkBatchId}
            label="Batch"
            onChange={(e) => setLinkBatchId(e.target.value)}
          >
            {linkBatches.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.batchNumber || b.id} — Stock: {b.leftInStockKg} kg
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          margin="normal"
          label="Quantity to link (kg)"
          type="number"
          value={linkQuantity}
          onChange={(e) => setLinkQuantity(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setLinkDialogOpen(false)} disabled={linkSaving}>Cancel</Button>
        <Button variant="contained" onClick={handleSaveLink} disabled={linkSaving}>
          {linkSaving ? <CircularProgress size={18} /> : "Link Batch"}
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
}

BatchAllocationsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  shippingInstruction: PropTypes.object,
};

export default BatchAllocationsDialog;
